import $ from 'jquery';
import _ from 'underscore';
import classNames from 'classnames';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SharedTile } from '@bingads-webui-campaign-react/shared-tile';
import { getBmcMtEndpoint, hasError } from '@bingads-webui-campaign/cpg-product-feed-grid';
import { Loading } from '@bingads-webui-campaign-react/loading';
import { getRouterDep } from '@bingads-webui-campaign-react/legacy-router-dep';

import { getDataTileStyles } from '../style';


const DataTileUnstyled = (props) => {
  const {
    classes,
    i18n,
    scope,
    shouldRefresh,
    setShouldRefresh,
    activity,
    logScope,
    brandsList,
    permissions,
  } = props;

  const { MranSimplificationV2 } = permissions.dynamic;

  const { aid, cid, uid } = scope.values;
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState({
    brandsSubmitted: 0,
    productsMatchedByBrands: 0,
    reportDownloadLink: '',
  });

  const navigateTo = useCallback((routeName, params) => {
    activity.trace(`${logScope}|NewSummaryPage|navigate to: campaign_create_objective`);
    getRouterDep(scope).navigateToRoute(routeName, params);
  }, [scope, activity, logScope]);

  const showTips = useMemo(() => _.findIndex(brandsList, brand => brand.MatchedProductCount === null) !== -1, [brandsList]);

  const footerTips = useMemo(() => {
    const tips = i18n.getString(MranSimplificationV2 ? _TL_('Product matching can take up to 10 hours.') : _TL_('We’re still processing your brands. This may take up to 24 hours.'));
    return (
      <div className={classNames(['alert', 'alert-info', classes.alertInfo])}>
        <div className="alert-image" />
        <div className="alert-info-inline" title={tips}>
          {tips}
        </div>
      </div>
    );
  }, [i18n, MranSimplificationV2, classes]);

  const tileConfig = useMemo(() => ({
    tileClassName: classes.dataTileContainer,
    header: {
      isRequired: true,
      renderHeaderText: () => <span className={classNames([classes.tileHeader])}>{ i18n.getString(_TL_('Matched products')) }</span>,
      i18n,
    },
    content: {
      styles: {
        root: {
          width: '606px',
          height: '330px',
          display: 'flex',
          flexDirection: 'column',
        },
      },
    },
    footer: {
      isRequired: true,
      buttons: [{
        text: i18n.getString(_TL_('Download report')),
        disabled: _.isEmpty(summaryData.reportDownloadLink),
        callback: () => {
          activity.trace(`${logScope}|NewSummaryPage|download report: ${summaryData.reportDownloadLink}`);
          window.open(summaryData.reportDownloadLink);
        },
      },
      {
        text: i18n.getString(_TL_('Create shopping for brands campaign')),
        callback: () => navigateTo('campaign_create_objective', { selected_item: 'SponsoredShoppingCampaign' }),
      }],
      footerTips: showTips ? footerTips : null,
    },
  }), [i18n, classes, summaryData, navigateTo, activity, logScope, footerTips, showTips]);

  const fetchSummaryData = useCallback(() => {
    setLoading(true);
    $.ajax({
      url: `${getBmcMtEndpoint()}/api/v1/manufacturers/${cid}/summary?cid=${cid}&aid=${aid}&uid=${uid}`,
      type: 'GET',
      xhrFields: {
        withCredentials: true,
      },
      success: (obj) => {
        if (!hasError(obj)) {
          setSummaryData(obj.ResponseObject);
        }
        setLoading(false);
      },
      error: () => {
        setLoading(false);
      },
    });
  }, [cid, aid, uid]);

  useEffect(() => {
    if (shouldRefresh) {
      fetchSummaryData();
      setShouldRefresh(false);
    }
  }, [fetchSummaryData, shouldRefresh, setShouldRefresh]);

  return (
    <div className={classNames([classes.dataTileContainer, showTips ? classes.showTips : '', 'data-tile'])}>
      {loading && <Loading isVisible />}
      <SharedTile config={tileConfig} classPrefix="data">
        <di className={classNames([classes.dataWrapper])}>
          <div className={classNames([classes.dataItem])}>
            <div className={classNames([classes.dataCount])}>{ summaryData.brandsSubmitted }</div>
            <div className={classNames([classes.dataDesc])}>{ i18n.getString(_TL_('Brands submitted')) }</div>
          </div>
          <div className={classNames([classes.dataItem])}>
            <div className={classNames([classes.dataCount])}>{ summaryData.productsMatchedByBrands }</div>
            <div className={classNames([classes.dataDesc])}>{ i18n.getString(_TL_('Products matched')) }</div>
          </div>
        </di>
      </SharedTile>
    </div>
  );
};

DataTileUnstyled.propTypes = {
  classes: PropTypes.shape({
    dataTileContainer: PropTypes.string.isRequired,
    dataWrapper: PropTypes.string.isRequired,
    dataItem: PropTypes.string.isRequired,
    dataCount: PropTypes.string.isRequired,
    dataDesc: PropTypes.string.isRequired,
    tileHeader: PropTypes.string.isRequired,
    alertInfo: PropTypes.string.isRequired,
    showTips: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      aid: PropTypes.string.isRequired,
      cid: PropTypes.string.isRequired,
      uid: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  shouldRefresh: PropTypes.bool.isRequired,
  setShouldRefresh: PropTypes.func.isRequired,
  activity: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }).isRequired,
  logScope: PropTypes.string.isRequired,
  brandsList: PropTypes.arrayOf(PropTypes.shape({
    MatchedProductCount: PropTypes.number,
  })).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      MranSimplificationV2: PropTypes.bool,
    }).isRequired,
  }).isRequired,
};

export const DataTile = withDefaultStyles(DataTileUnstyled, getDataTileStyles);
